/**
 * Price-alert Redux slice.
 *
 * Manages user-defined price alerts and the in-app notification inbox.
 * Both collections are persisted to `localStorage` so they survive
 * page refreshes and app restarts.
 *
 * ### Alert lifecycle
 * 1. `addAlert` — creates the alert with a generated ID.
 * 2. `AlertEvaluationEngine` (service layer) polls every 5 s and dispatches
 *    `triggerAlert` when the condition is met.
 * 3. `triggerAlert` marks the alert as triggered, creates a `Notification`,
 *    and for repeating alerts resets the trigger state for the next crossing.
 *
 * Notifications are trimmed to the most recent 100 on each save to cap
 * `localStorage` usage.
 */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/** A single configurable price alert. */
export interface PriceAlert {
  id: string;
  symbol: string;
  condition: "above" | "below" | "crosses_above" | "crosses_below";
  targetPrice: number;
  /** Price at the time the alert was created (for cross detection) */
  createdAtPrice: number;
  createdAt: number; // timestamp
  triggered: boolean;
  triggeredAt?: number;
  /** Whether to repeat the alert after trigger */
  repeating: boolean;
  /** Optional user note */
  note?: string;
}

/** An in-app notification entry. */
export interface Notification {
  id: string;
  type: "alert" | "system" | "info";
  title: string;
  message: string;
  timestamp: number;
  read: boolean;
  /** Optional link to symbol */
  symbol?: string;
  alertId?: string;
}

/** Shape of the alert/notification state tree. */
export interface AlertState {
  alerts: PriceAlert[];
  notifications: Notification[];
  unreadCount: number;
  alertSoundEnabled: boolean;
}

const STORAGE_KEY = "marketview_price_alerts";
const NOTIF_STORAGE_KEY = "marketview_notifications";
const ALERT_SOUND_STORAGE_KEY = "marketview_alert_sound_enabled";

function loadAlerts(): PriceAlert[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveAlerts(alerts: PriceAlert[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(alerts));
  } catch {}
}

function loadNotifications(): Notification[] {
  try {
    const raw = localStorage.getItem(NOTIF_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveNotifications(notifications: Notification[]) {
  try {
    // Keep max 100 notifications
    const trimmed = notifications.slice(-100);
    localStorage.setItem(NOTIF_STORAGE_KEY, JSON.stringify(trimmed));
  } catch {}
}

function loadAlertSoundEnabled(): boolean {
  try {
    const raw = localStorage.getItem(ALERT_SOUND_STORAGE_KEY);
    if (raw == null) return true;
    return raw === "true";
  } catch {
    return true;
  }
}

function saveAlertSoundEnabled(enabled: boolean) {
  try {
    localStorage.setItem(ALERT_SOUND_STORAGE_KEY, String(enabled));
  } catch {}
}

const savedAlerts = loadAlerts();
const savedNotifications = loadNotifications();
const savedAlertSoundEnabled = loadAlertSoundEnabled();

const initialState: AlertState = {
  alerts: savedAlerts,
  notifications: savedNotifications,
  unreadCount: savedNotifications.filter(n => !n.read).length,
  alertSoundEnabled: savedAlertSoundEnabled,
};

export const alertSlice = createSlice({
  name: "alerts",
  initialState,
  reducers: {
    addAlert: (state, action: PayloadAction<Omit<PriceAlert, "id" | "createdAt" | "triggered">>) => {
      const alert: PriceAlert = {
        ...action.payload,
        id: `alert_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        createdAt: Date.now(),
        triggered: false,
      };
      state.alerts.push(alert);
      saveAlerts(state.alerts);
    },
    removeAlert: (state, action: PayloadAction<string>) => {
      state.alerts = state.alerts.filter(a => a.id !== action.payload);
      saveAlerts(state.alerts);
    },
    triggerAlert: (state, action: PayloadAction<{ alertId: string; currentPrice: number }>) => {
      const { alertId, currentPrice } = action.payload;
      const alert = state.alerts.find(a => a.id === alertId);
      if (!alert) return;
      
      alert.triggered = true;
      alert.triggeredAt = Date.now();

      // Create notification
      const notification: Notification = {
        id: `notif_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        type: "alert",
        title: `Price Alert: ${alert.symbol}`,
        message: `${alert.symbol} ${alert.condition.replace("_", " ")} ₹${alert.targetPrice.toLocaleString("en-IN")} (LTP: ₹${currentPrice.toLocaleString("en-IN")})`,
        timestamp: Date.now(),
        read: false,
        symbol: alert.symbol,
        alertId: alert.id,
      };
      state.notifications.push(notification);
      state.unreadCount++;

      // Remove non-repeating alerts after trigger
      if (!alert.repeating) {
        state.alerts = state.alerts.filter(a => a.id !== alertId);
      } else {
        // Reset for next trigger
        alert.triggered = false;
        alert.createdAtPrice = currentPrice;
      }

      saveAlerts(state.alerts);
      saveNotifications(state.notifications);
    },
    clearAllAlerts: (state) => {
      state.alerts = [];
      saveAlerts(state.alerts);
    },
    addNotification: (state, action: PayloadAction<Omit<Notification, "id" | "timestamp" | "read">>) => {
      const notification: Notification = {
        ...action.payload,
        id: `notif_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        timestamp: Date.now(),
        read: false,
      };
      state.notifications.push(notification);
      state.unreadCount++;
      saveNotifications(state.notifications);
    },
    markNotificationRead: (state, action: PayloadAction<string>) => {
      const notif = state.notifications.find(n => n.id === action.payload);
      if (notif && !notif.read) {
        notif.read = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
        saveNotifications(state.notifications);
      }
    },
    markAllNotificationsRead: (state) => {
      state.notifications.forEach(n => { n.read = true; });
      state.unreadCount = 0;
      saveNotifications(state.notifications);
    },
    clearAllNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
      saveNotifications(state.notifications);
    },
    toggleAlertSound: (state) => {
      state.alertSoundEnabled = !state.alertSoundEnabled;
      saveAlertSoundEnabled(state.alertSoundEnabled);
    },
    setAlertSoundEnabled: (state, action: PayloadAction<boolean>) => {
      state.alertSoundEnabled = action.payload;
      saveAlertSoundEnabled(state.alertSoundEnabled);
    },
  },
});

export const {
  addAlert,
  removeAlert,
  triggerAlert,
  clearAllAlerts,
  addNotification,
  markNotificationRead,
  markAllNotificationsRead,
  clearAllNotifications,
  toggleAlertSound,
  setAlertSoundEnabled,
} = alertSlice.actions;

export default alertSlice.reducer;
